import React from "react";
import { Mountain, Quote, MapPin, ShieldCheck, TrendingUp, Users, Printer, ScrollText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { mineMeta, kpiSummary, investmentSnapshot, tzaneenInfo } from "@/data/mineData";

export default function Memorandum() {
  return (
    <div data-testid="page-memorandum">
      {/* Document header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-amber-700 font-semibold">
          <ScrollText size={14} />
          Confidential Information Memorandum
        </div>
        <Button
          onClick={() => window.print()}
          data-testid="im-print-button"
          variant="outline"
          className="border-slate-300"
        >
          <Printer size={14} className="mr-2" />
          Print / Save PDF
        </Button>
      </div>

      {/* Hero */}
      <div className="relative rounded-lg overflow-hidden mb-8 bg-slate-900">
        <img
          src="https://images.unsplash.com/photo-1776012203102-a024822685cc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxhZXJpYWwlMjB2aWV3JTIwb3BlbiUyMHBpdCUyMG1pbmV8ZW58MHx8fHwxNzc3NDU4NTE0fDA&ixlib=rb-4.1.0&q=85"
          alt="Aerial view"
          className="absolute inset-0 w-full h-full object-cover opacity-50"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-slate-950 via-slate-900/70 to-transparent" />
        <div className="relative p-8 md:p-12 text-white">
          <div className="flex items-center gap-2 mb-4">
            <div className="h-8 w-8 rounded-md bg-white/10 backdrop-blur flex items-center justify-center">
              <Mountain size={16} className="text-amber-400" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-amber-400 font-semibold">
              Project Tzaneen · Limpopo, South Africa
            </div>
          </div>
          <h1 className="font-display text-4xl md:text-5xl xl:text-6xl font-semibold tracking-tight max-w-4xl leading-[1.05]">
            A 1.24M-ton-per-year, multi-commodity operation — for sale.
          </h1>
          <p className="mt-6 text-base md:text-lg text-slate-200 max-w-2xl leading-relaxed">
            Cash-generative, operationally mature, and located in the heart of South Africa's mining belt.
            EBITDA margins have expanded from 22.6% to 33.8% in five years.
          </p>

          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl pt-8 border-t border-white/15">
            <HeroStat label="Annual Production" value={kpiSummary.annualProduction.value} unit={kpiSummary.annualProduction.unit} />
            <HeroStat label="Estimated Revenue" value={kpiSummary.estimatedRevenue.value} unit="ZAR per year" />
            <HeroStat label="Reserve Life" value={kpiSummary.reserveLife.value} unit="remaining" />
            <HeroStat label="Asking Price" value={investmentSnapshot.askingPrice} unit="ZAR · 5.0× EBITDA" />
          </div>
        </div>
      </div>

      {/* Pull-quote */}
      <div className="bg-amber-50 border-l-4 border-amber-700 rounded-r-md p-6 mb-8" data-testid="im-pullquote">
        <Quote size={24} className="text-amber-700 mb-3" />
        <p className="font-display text-xl md:text-2xl text-slate-900 leading-snug max-w-3xl">
          "Tzaneen is a textbook mid-tier mining asset: predictable cash flow, identified upside levers, and a clean legal book. The next operator inherits a platform — not a project."
        </p>
        <p className="mt-4 text-xs uppercase tracking-wider text-amber-700 font-semibold">
          — Vendor Advisory Team, Sale Memorandum 2025
        </p>
      </div>

      {/* The Opportunity */}
      <Section number="01" title="The Opportunity">
        <p className="text-base text-slate-700 leading-relaxed mb-4">
          Tzaneen Mineral Project is a fully-permitted, operational mining complex producing a balanced portfolio of <span className="font-semibold text-slate-900">coal, platinum-group metals, and chrome</span> from a single, contiguous tenement on the eastern limb of the Bushveld Igneous Complex. The asset has operated continuously since 2008 and is being offered as a complete going concern — including all mining rights, equipment, infrastructure, off-take contracts, and a workforce of 1,240.
        </p>
        <p className="text-base text-slate-700 leading-relaxed mb-4">
          For a buyer, this is a rare combination. The asset is <span className="font-semibold text-slate-900">large enough to matter</span> — 1.24M tons of annual production and R3.4bn in revenue — yet <span className="font-semibold text-slate-900">small enough to operate without a major's overhead</span>. Reserve life of 18 years at current run-rate provides a long runway, while identified opportunities (crusher debottlenecking, solar PPA, reserve upgrades) offer a credible path to a R7.12bn valuation case.
        </p>
        <p className="text-base text-slate-700 leading-relaxed">
          The vendor is selling for portfolio reasons, not asset reasons. The mine is profitable, growing, and not encumbered by litigation, capex overhang, or community disputes.
        </p>
      </Section>

      {/* Why Tzaneen — pillars */}
      <Section number="02" title="Why Tzaneen">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Pillar
            icon={MapPin}
            title="Strategic location"
            body="Mopani District, Limpopo — within the Bushveld Igneous Complex. 14 km to Transnet rail siding. 480 km to Maputo deep-water port via N4 corridor. 132 kV Eskom grid connection in place."
          />
          <Pillar
            icon={TrendingUp}
            title="Strong economics"
            body="EBITDA margin expanded from 22.6% (2020) to 33.8% (2024). Cost per ton trending down despite Eskom tariff pressure. Cash conversion above 80%."
          />
          <Pillar
            icon={ShieldCheck}
            title="Clean legal book"
            body="Mining right valid through 2042. All licenses current except one EA renewal in process. No active litigation. Section 11 transfer approvals already secured."
          />
          <Pillar
            icon={Users}
            title="Established community structure"
            body="BBBEE Level 3 verified. Royalty agreement with Limpopo Community Trust at 1.5% gross. Workforce of 1,240 with low turnover and a deep local skills catchment."
          />
        </div>
      </Section>

      {/* Setting / Geography */}
      <Section number="03" title="The Setting">
        <p className="text-base text-slate-700 leading-relaxed mb-5">
          Tzaneen sits in {tzaneenInfo.district}, approximately {tzaneenInfo.distanceFromPolokwane} of Polokwane, the provincial capital. The geology is favourable — a layered intrusive complex that hosts coal seams at surface, a Bushveld PGM reef beneath, and chromitite layers below that. Subtropical highveld climate with {tzaneenInfo.climate} provides reliable year-round operations with limited weather-related disruption.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { label: "Geological Setting", value: tzaneenInfo.geologicalSetting },
            { label: "Elevation", value: tzaneenInfo.elevation },
            { label: "Rail", value: tzaneenInfo.rail },
            { label: "Power", value: tzaneenInfo.power },
            { label: "Catchment", value: tzaneenInfo.populationCatchment },
            { label: "Community", value: tzaneenInfo.community },
          ].map((f) => (
            <div key={f.label} className="bg-slate-50 border border-slate-100 rounded-md p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</div>
              <div className="text-sm text-slate-800 mt-1">{f.value}</div>
            </div>
          ))}
        </div>
      </Section>

      {/* Transaction Structure */}
      <Section number="04" title="Transaction Structure">
        <div className="bg-white border border-slate-200 rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <DealRow label="Asking price" value={investmentSnapshot.askingPrice} sub="ZAR, debt-free / cash-free basis" />
            <DealRow label="Implied EBITDA multiple" value={investmentSnapshot.ebitdaMultiple} sub="vs sector mid-cap median 4.8×" />
            <DealRow label="Payback period" value={investmentSnapshot.paybackPeriod} sub="at current run-rate cash flow" />
            <DealRow label="Projected IRR" value={investmentSnapshot.irr} sub="base case, 10-year hold" />
            <DealRow label="Process" value="Two-stage" sub="Indicative bids → confirmatory diligence" />
            <DealRow label="Target close" value="Q4 2025" sub="Subject to regulatory approvals" />
          </div>

          <div className="mt-6 pt-5 border-t border-slate-200 text-sm text-slate-600 leading-relaxed">
            The vendor will entertain <span className="font-semibold text-slate-900">100% disposal</span> as the preferred outcome, with consideration given to staged or earn-out structures for strategic buyers.
            Indicative bids are due by <span className="font-semibold text-slate-900">15 June 2025</span>. Access to the management team and confirmatory diligence will be granted to shortlisted parties.
          </div>
        </div>
      </Section>

      {/* Closing CTA */}
      <div className="bg-slate-900 rounded-lg p-8 md:p-12 text-white text-center mt-10 grain relative overflow-hidden">
        <div className="text-[11px] uppercase tracking-[0.25em] text-amber-400 font-semibold mb-3">
          Next steps
        </div>
        <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-tight max-w-3xl mx-auto">
          Review the data room. Meet the team. Make the case to your IC.
        </h3>
        <p className="mt-4 text-sm text-slate-300 max-w-2xl mx-auto">
          All supporting documents are available in the Document Data Room. AI-informed risk insights are continuously refreshed in the Risk &amp; Insights view.
        </p>
        <div className="mt-6 text-xs text-slate-400">
          Confidentiality obligations under the executed NDA apply to this memorandum and all related materials.
        </div>
      </div>
    </div>
  );
}

function HeroStat({ label, value, unit }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-amber-400 font-semibold">{label}</div>
      <div className="font-display text-2xl md:text-3xl font-bold mt-1">{value}</div>
      <div className="text-[10px] text-white/60 uppercase tracking-wider mt-0.5">{unit}</div>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="mb-10" data-testid={`im-section-${number}`}>
      <div className="flex items-baseline gap-4 mb-5 pb-3 border-b border-slate-200">
        <span className="font-display text-3xl font-bold text-amber-700 tabular-nums">{number}</span>
        <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Pillar({ icon: Icon, title, body }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center gap-3 mb-3">
        <div className="h-9 w-9 rounded-md bg-amber-50 border border-amber-200 flex items-center justify-center">
          <Icon size={16} className="text-amber-700" />
        </div>
        <h4 className="font-display text-base font-semibold text-slate-900">{title}</h4>
      </div>
      <p className="text-sm text-slate-700 leading-relaxed">{body}</p>
    </div>
  );
}

function DealRow({ label, value, sub }) {
  return (
    <div className="border-b border-slate-100 pb-4 last:border-b-0 last:pb-0">
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-bold text-slate-900 mt-1">{value}</div>
      <div className="text-xs text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}
